import React, { useEffect, useRef, useState } from 'react';
import './ValleyOfPeace.css';

/* ──────────────────────────────────────────────────────────────
   Constants
   ────────────────────────────────────────────────────────────── */
const TAU = Math.PI * 2;
const VW = 1440;          // SVG viewBox width
const VH = 900;           // SVG viewBox height
const WAVE_POINTS = 80;   // resolution of each wave path
const TRAIL_MAX = 8;      // data packet trail length
const TRAIL_MIN_DIST = 14; // min px between trail dots

/* ──────────────────────────────────────────────────────────────
   Narration captions
   ────────────────────────────────────────────────────────────── */
const CAPTIONS = {
  idle:       null,
  input:      'Your data takes shape…',
  analyzing:  'Riding the signal…',
  scored:     'Clarity in the calm…',
  optimizing: 'Diving deeper…',
  complete:   'Transmission complete.',
};

/* ──────────────────────────────────────────────────────────────
   Wave layer geometry (back → front)
   ────────────────────────────────────────────────────────────── */
const WAVE_LAYERS = [
  { yBase: 0.58, amplitude: 38, frequency: 1.1, phase: 0.0,  speedFactor: 1.0  },
  { yBase: 0.64, amplitude: 32, frequency: 1.4, phase: 0.45, speedFactor: 0.82 },
  { yBase: 0.70, amplitude: 26, frequency: 1.7, phase: 0.95, speedFactor: 0.68 },
  { yBase: 0.77, amplitude: 20, frequency: 2.0, phase: 1.45, speedFactor: 0.52 },
  { yBase: 0.85, amplitude: 14, frequency: 2.3, phase: 2.00, speedFactor: 0.38 },
];

/* ──────────────────────────────────────────────────────────────
   Phase configurations (controls speed, amplitude, packet)
   ────────────────────────────────────────────────────────────── */
const PHASE_CONFIGS = {
  idle:       { speed: 0.025, ampMult: 0.30, packetVisible: false, packetX: 0.15 },
  input:      { speed: 0.04,  ampMult: 0.35, packetVisible: true,  packetX: 0.18 },
  analyzing:  { speed: 0.06,  ampMult: 0.45, packetVisible: true,  packetX: null, packetMoving: true,  oscRange: [0.18, 0.52] },
  scored:     { speed: 0.02,  ampMult: 0.30, packetVisible: true,  packetX: 0.55 },
  optimizing: { speed: 0.04,  ampMult: 0.40, packetVisible: true,  packetX: null, packetMoving: true,  oscRange: [0.52, 0.82] },
  complete:   { speed: 0.015, ampMult: 0.25, packetVisible: true,  packetX: 0.82 },
};

/* ──────────────────────────────────────────────────────────────
   Ambient particles — fixed positions with CSS animation
   ────────────────────────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  cx: 80 + (i * 103) % VW,
  cy: (VH * 0.50) + (i * 47) % 200,
  r: 1.2 + (i % 3) * 0.5,
  dur: 30 + (i % 5) * 8,
  delay: -(i * 4.5),
  drift: (i % 2 === 0 ? 1 : -1) * (10 + (i % 4) * 8),
}));

/* ──────────────────────────────────────────────────────────────
   Wave math helpers
   ────────────────────────────────────────────────────────────── */
function getWaveY(time, wave, nx, speed, ampMult) {
  const a = wave.amplitude * ampMult;
  const t = time * wave.speedFactor;
  return wave.yBase * VH
    + a       * Math.sin(TAU * (nx * wave.frequency + t + wave.phase))
    + a * 0.3 * Math.sin(TAU * (nx * wave.frequency * 2.1 + t * 1.3 + wave.phase * 0.7))
    + a * 0.1 * Math.sin(TAU * (nx * wave.frequency * 3.5 + t * 0.6 + wave.phase * 1.4));
}

function buildFillPath(time, wave, speed, ampMult) {
  let d = '';
  for (let i = 0; i <= WAVE_POINTS; i++) {
    const nx = i / WAVE_POINTS;
    const x = nx * VW;
    const y = getWaveY(time, wave, nx, speed, ampMult);
    d += i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`;
  }
  d += `L${VW},${VH}L0,${VH}Z`;
  return d;
}

function buildLinePath(time, wave, speed, ampMult) {
  let d = '';
  for (let i = 0; i <= WAVE_POINTS; i++) {
    const nx = i / WAVE_POINTS;
    const x = nx * VW;
    const y = getWaveY(time, wave, nx, speed, ampMult);
    d += i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`;
  }
  return d;
}

/* ──────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────── */
const ValleyOfPeace = ({ phase = 'idle' }) => {
  const svgRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    time: 0,
    speed: 0.25,
    targetSpeed: 0.25,
    ampMult: 0.65,
    targetAmpMult: 0.65,
    packetX: 0.15,
    targetPacketX: 0.15,
    packetVisible: false,
    packetMoving: false,
    flightLerp: 0,
    oscRange: [0.18, 0.52],
    trail: [],
    phase: 'idle',
  });

  const [captionKey, setCaptionKey] = useState(0);
  const [visibleCaption, setVisibleCaption] = useState(null);
  const prevPhaseRef = useRef(phase);

  /* ── Phase change handler ── */
  useEffect(() => {
    const cfg = PHASE_CONFIGS[phase] || PHASE_CONFIGS.idle;
    const s = stateRef.current;
    s.targetSpeed = cfg.speed;
    s.targetAmpMult = cfg.ampMult;
    s.packetVisible = cfg.packetVisible;
    s.packetMoving = cfg.packetMoving || false;
    s.phase = phase;
    if (cfg.oscRange) s.oscRange = cfg.oscRange;
    if (cfg.packetX != null) s.targetPacketX = cfg.packetX;

    // Caption
    if (prevPhaseRef.current !== phase) {
      prevPhaseRef.current = phase;
      const caption = CAPTIONS[phase];
      if (caption) {
        setVisibleCaption(caption);
        setCaptionKey(k => k + 1);
        const timer = setTimeout(() => setVisibleCaption(null), 5500);
        return () => clearTimeout(timer);
      }
    }
  }, [phase]);

  /* ── Animation loop ── */
  useEffect(() => {
    let lastTime = performance.now();

    const animate = (now) => {
      const dt = Math.min((now - lastTime) / 1000, 0.05); // cap dt at 50ms
      lastTime = now;

      const s = stateRef.current;
      const svg = svgRef.current;
      if (!svg) { rafRef.current = requestAnimationFrame(animate); return; }

      /* Lerp speed & amplitude toward targets - much smoother now */
      const lerpFactor = 1 - Math.pow(0.5, dt);
      s.speed += (s.targetSpeed - s.speed) * lerpFactor;
      s.ampMult += (s.targetAmpMult - s.ampMult) * lerpFactor;

      /* Advance time */
      s.time += s.speed * dt;

      /* Flight lerp for detaching from wave */
      if (s.packetMoving) {
        s.flightLerp += (1 - s.flightLerp) * (lerpFactor * 2.0);
      } else {
        s.flightLerp += (0 - s.flightLerp) * (lerpFactor * 1.5);
      }

      const [lo, hi] = s.oscRange;
      const mid = (lo + hi) / 2;
      const half = (hi - lo) / 2;

      /* Packet X position */
      if (s.packetMoving) {
        s.packetX = mid + half * Math.sin(s.time * 15.0);
      } else {
        s.packetX += (s.targetPacketX - s.packetX) * Math.min(lerpFactor * 1.5, 0.08);
      }

      /* ── Update wave SVG paths ── */
      for (let i = 0; i < WAVE_LAYERS.length; i++) {
        const wave = WAVE_LAYERS[i];
        const fillEl = svg.querySelector(`#vw-f-${i}`);
        const lineEl = svg.querySelector(`#vw-l-${i}`);
        if (fillEl) fillEl.setAttribute('d', buildFillPath(s.time, wave, s.speed, s.ampMult));
        if (lineEl) lineEl.setAttribute('d', buildLinePath(s.time, wave, s.speed, s.ampMult));
      }

      /* ── Update data packet ── */
      const packetG = svg.querySelector('#v-packet');
      if (packetG) {
        if (s.packetVisible) {
          let nx = s.packetX;
          const topWave = WAVE_LAYERS[0];
          let py = getWaveY(s.time, topWave, nx, s.speed, s.ampMult) - 12;
          let px = nx * VW;
          let angle = 0;

          if (s.flightLerp > 0.001) {
            // Detach and fly in an arc towards the top-left logo
            const sineVal = Math.sin(s.time * 15.0); // -1 (left side of range) to +1 (right side)
            const flightIntensity = ((1 - sineVal) / 2) * s.flightLerp; // 1 at left, 0 at right
            
            const logoX = 140; // Approx X of RezFlow logo
            const logoY = 90;  // Approx Y of RezFlow logo
            
            px = px - (px - logoX) * flightIntensity;
            py = py - (py - logoY) * flightIntensity;
            
            // Finite difference for trajectory angle
            const dt_sim = 0.01;
            const nextTime = s.time + dt_sim;
            const nextSineVal = Math.sin(nextTime * 15.0);
            const nextNx = mid + half * nextSineVal;
            let nextPx = nextNx * VW;
            let nextPy = getWaveY(nextTime, topWave, nextNx, s.speed, s.ampMult) - 12;
            const nextFlightIntensity = ((1 - nextSineVal) / 2) * s.flightLerp;
            
            nextPx = nextPx - (nextPx - logoX) * nextFlightIntensity;
            nextPy = nextPy - (nextPy - logoY) * nextFlightIntensity;
            
            angle = Math.atan2(nextPy - py, nextPx - px) * (180 / Math.PI);
          } else {
            // Riding the wave
            const dx = 0.005;
            const py2 = getWaveY(s.time, topWave, nx + dx, s.speed, s.ampMult) - 12;
            angle = Math.atan2(py2 - py, dx * VW) * (180 / Math.PI);
          }

          packetG.setAttribute('transform', `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${angle.toFixed(1)})`);
          packetG.style.opacity = '1';

          /* Trail dots */
          const lastTrail = s.trail[s.trail.length - 1];
          const dist = lastTrail ? Math.hypot(px - lastTrail.x, py - lastTrail.y) : 999;
          if (dist > TRAIL_MIN_DIST) {
            s.trail.push({ x: px, y: py });
            if (s.trail.length > TRAIL_MAX) s.trail.shift();
          }

          for (let ti = 0; ti < TRAIL_MAX; ti++) {
            const dot = svg.querySelector(`#v-trail-${ti}`);
            if (!dot) continue;
            const tp = s.trail[ti];
            if (tp) {
              const opacity = ((ti + 1) / s.trail.length) * 0.35;
              const r = 2.5 - (TRAIL_MAX - 1 - ti) * 0.25;
              dot.setAttribute('cx', tp.x.toFixed(1));
              dot.setAttribute('cy', tp.y.toFixed(1));
              dot.setAttribute('r', Math.max(r, 0.8).toFixed(1));
              dot.setAttribute('opacity', opacity.toFixed(2));
            } else {
              dot.setAttribute('opacity', '0');
            }
          }
        } else {
          packetG.style.opacity = '0';
          s.trail.length = 0;
          for (let ti = 0; ti < TRAIL_MAX; ti++) {
            const dot = svg.querySelector(`#v-trail-${ti}`);
            if (dot) dot.setAttribute('opacity', '0');
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const phaseClass = `valley-phase-${phase}`;

  return (
    <>
      <div className={`valley-scene ${phaseClass}`} aria-hidden="true" role="presentation">
        {/* Solid background */}
        <div className="valley-bg" />

        {/* Glow overlays */}
        <div className="valley-glow valley-glow-jade" />
        <div className="valley-glow valley-glow-gold" />
        <div className="valley-glow valley-glow-aqua" />

        {/* Main SVG */}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          style={{ position: 'absolute', inset: 0 }}
        >
          <defs>
            <radialGradient id="v-pkt-glow">
              <stop offset="0%"   stopColor="currentColor" stopOpacity="0.55" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
            <filter id="v-glow">
              <feGaussianBlur stdDeviation="2.5" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* ── Ambient particles ── */}
          {PARTICLES.map((p, i) => (
            <circle
              key={i}
              cx={p.cx}
              cy={p.cy}
              r={p.r}
              className="valley-particle"
              style={{
                animationDuration: `${p.dur}s`,
                animationDelay: `${p.delay}s`,
                '--px-drift': `${p.drift}px`,
              }}
            />
          ))}

          {/* ── Wave layers (back → front) ── */}
          {WAVE_LAYERS.map((_, i) => (
            <React.Fragment key={i}>
              <path
                id={`vw-f-${i}`}
                className={`valley-wave valley-wave-${i}`}
              />
              <path
                id={`vw-l-${i}`}
                className={`valley-wave-hl valley-wave-hl-${i}`}
                fill="none"
                strokeWidth={1.8 - i * 0.25}
              />
            </React.Fragment>
          ))}

          {/* ── Trail dots ── */}
          {Array.from({ length: TRAIL_MAX }, (_, i) => (
            <circle
              key={i}
              id={`v-trail-${i}`}
              className="valley-trail-dot"
              cx="0"
              cy="0"
              r="2"
              opacity="0"
            />
          ))}

          {/* ── Data Packet ── */}
          <g id="v-packet" className="valley-packet" style={{ opacity: 0 }}>
            {/* Outer glow aura */}
            <circle r="24" fill="url(#v-pkt-glow)" className="valley-packet-aura" />
            {/* Hexagonal body */}
            <polygon
              points="0,-9 7.8,-4.5 7.8,4.5 0,9 -7.8,4.5 -7.8,-4.5"
              className="valley-packet-body"
              filter="url(#v-glow)"
            />
            {/* Inner core */}
            <polygon
              points="0,-4.5 3.9,-2.25 3.9,2.25 0,4.5 -3.9,2.25 -3.9,-2.25"
              className="valley-packet-core"
            />
            {/* Pulse ring */}
            <circle
              r="14"
              strokeWidth="1.2"
              className="valley-packet-ring"
            />
          </g>
        </svg>
      </div>

      {/* ── Narration caption ── */}
      {visibleCaption && (
        <div
          key={captionKey}
          className={`valley-caption ${phaseClass}`}
          aria-live="polite"
          aria-atomic="true"
        >
          {visibleCaption}
        </div>
      )}
    </>
  );
};

export default ValleyOfPeace;
